import React, { useEffect, useState } from 'react';
import { Flex, Table } from 'antd';

import useData from '../../hooks/utility/useData';
import PhoneNumberInput from '../../components/form/PhoneNumberInput';
import { debounce } from 'lodash';
import { PaginatedResponseData } from '../../services/api/client';
import PaginationHelpers from '../../services/utils/pagination/PaginationHelpers';
import { IBlockedPhoneNumber } from '../../types/customer';
import { loadBlockedPhoneNumbers } from '../../services/api/CustomerService';
import useHelpers from '../../hooks/utility/useHelpers';
import ToggleBlockPhoneNumberButton from '../../components/customer/ToggleBlockPhoneNumberButton';
import collect from 'collect.js';
import moment from 'moment-timezone';

const PhoneNumberForceRenderKey = 'blocked_numbers.phone_number';

export default function BlockedNumbersPage() {
  const { formatDateTime } = useHelpers();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponseData<IBlockedPhoneNumber>>(
    PaginationHelpers.initPaginationData(),
  );

  const { data: filters, updateData: updateFilter } = useData({
    phone_number: null,
  });

  const debounced = debounce(async () => loadData(data.meta.current_page), 500);

  useEffect(() => {
    debounced();

    return () => debounced.cancel();
  }, [filters]);

  async function loadData(page: number) {
    setLoading(true);

    loadBlockedPhoneNumbers({
      page,
      filter: filters,
    })
      .then((_data) => setData(_data.data))
      .catch((error) => console.error('Failed to load blocked phone numbers', { error }))
      .finally(() => setLoading(false));
  }

  const onBlockToggleSuccess = (phoneNumber: string) => {
    const item = collect(data.data).first((item) => item.phone_number === phoneNumber);

    if (!item) {
      return setData((current) => ({
        ...current,
        data: [
          {
            phone_number: phoneNumber,
            blocked_at: moment().toISOString(),
          },
          ...current.data,
        ],
      }));
    }

    setData((current) => ({
      ...current,
      data: collect(current.data)
        .map((_item) =>
          _item.phone_number === item.phone_number
            ? { ..._item, blocked_at: _item.blocked_at ? null : moment().toISOString() }
            : item,
        )
        .toArray(),
    }));
  };

  return (
    <>
      <h1>Blocked numbers</h1>
      <div className="mt-lg">
        <Flex className="w-full gap-md">
          <PhoneNumberInput
            distinct
            country="de"
            enableSearch
            preferredCountries={['de']}
            forwardKey={PhoneNumberForceRenderKey}
            value={filters.phone_number}
            onChange={(value: string) => updateFilter('phone_number', value)}
          />
          <ToggleBlockPhoneNumberButton
            isBlocked={false}
            phoneNumber={filters.phone_number}
            onSuccess={() => onBlockToggleSuccess(filters.phone_number)}
          />
        </Flex>
        <Table
          dataSource={data.data}
          loading={loading}
          rowKey="id"
          className="mt-sm overflow-x-auto"
          pagination={{
            current: data.meta.current_page,
            total: data.meta.total,
            pageSize: data.meta.per_page,
            onChange: (page) => loadData(page),
            rootClassName: 'tw-overflow-hidden',
          }}
          columns={[
            {
              key: 'phone_number',
              title: 'Phone number',
              dataIndex: 'phone_number',
            },
            {
              key: 'blocked_at',
              title: 'Blocked at',
              render: (item) => (item.blocked_at ? formatDateTime(item.blocked_at) : '-'),
            },
            {
              key: 'actions',
              render: (_, item) => (
                <ToggleBlockPhoneNumberButton
                  phoneNumber={item.phone_number}
                  isBlocked={item.blocked_at !== null}
                  onSuccess={() => onBlockToggleSuccess(item.phone_number)}
                />
              ),
            },
          ]}
        />
      </div>
    </>
  );
}
